import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Stack, Button, CircularProgress, Alert } from '@mui/material';
import { Save, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import WrapperSection from '../global/WrapperSection';
import GlobalBreadcrumbs from '../global/GlobalBreadcrumbs';
import LanguageToggle from '../global/LanguageToggle';
import { useGetSettings } from '../../hooks/settings/useGetSettings';
import { useUpdateSettings } from '../../hooks/settings/useUpdateSettings';
import SettingsSectionCard from './SettingsSectionCard';

// Walks any value and returns true if at least one nested field is a
// bilingual {ar?, en?} object — that's the trigger for showing the
// LanguageToggle in the page header.
const hasBilingualField = (val) => {
    if (val === null || val === undefined) return false;
    if (typeof val !== 'object') return false;
    if (Array.isArray(val)) return val.some(hasBilingualField);
    if ('ar' in val || 'en' in val) {
        // Treat as bilingual only when ar/en are strings (or empty), not
        // arbitrary nested objects (e.g. payment.paymob also contains keys
        // we don't want to misclassify).
        const arStr = typeof val.ar === 'string' || val.ar === undefined;
        const enStr = typeof val.en === 'string' || val.en === undefined;
        if (arStr && enStr) return true;
    }
    return Object.values(val).some(hasBilingualField);
};

/**
 * Diff helper — returns a grouped object containing only the section/field
 * pairs that differ between `draft` and `original`. Limited to the sections
 * passed in `sectionKeys` so a page only saves what it actually owns.
 */
const buildPatch = (original, draft, sectionKeys) => {
    const patch = {};
    sectionKeys.forEach((sectionKey) => {
        const orig = original?.[sectionKey] || {};
        const next = draft?.[sectionKey] || {};
        const changed = {};
        Object.keys(next).forEach((field) => {
            const a = orig[field];
            const b = next[field];
            if (JSON.stringify(a) !== JSON.stringify(b)) {
                changed[field] = b;
            }
        });
        if (Object.keys(changed).length > 0) {
            patch[sectionKey] = changed;
        }
    });
    return patch;
};

const SettingsPage = ({ titleEn, titleAr, descriptionEn, descriptionAr, sections, extraActions }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const title = isArabic ? titleAr : titleEn;
    const description = isArabic ? descriptionAr : descriptionEn;

    const { settings, isLoading, error } = useGetSettings();
    const updateMutation = useUpdateSettings();

    const [draft, setDraft] = useState({});
    const initialLang =
        (localStorage.getItem('i18nextLng') || i18next.language || 'ar').startsWith('ar')
            ? 'ar'
            : 'en';
    const [activeLang, setActiveLang] = useState(initialLang);

    useEffect(() => {
        // (re)initialize the draft whenever the server data changes.
        // For sections that declare `defaultFields`, fill in any keys the
        // backend hasn't seeded yet so the admin can write them. The PATCH
        // diff in buildPatch() then sends only the keys that actually
        // changed — untouched defaults stay out of the request.
        const next = { ...(settings || {}) };
        sections.forEach((s) => {
            if (!s.defaultFields) return;
            const current = next[s.key] && typeof next[s.key] === 'object' ? next[s.key] : {};
            const merged = { ...s.defaultFields, ...current };
            next[s.key] = merged;
        });
        setDraft(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings, sections]);

    const sectionKeys = useMemo(() => sections.map((s) => s.key), [sections]);

    // Show the LanguageToggle only when one of this page's sections actually
    // contains a bilingual field (avoids a useless toggle on pages like
    // Integrations whose values are all flat strings).
    const BILINGUAL_FIELD_KEYS = useMemo(() => new Set(['site_name', 'address', 'footer_description']), []);

    const showLanguageToggle = useMemo(
        () => sectionKeys.some((k) => {
            if (hasBilingualField(draft?.[k])) return true;
            const section = sections.find(s => s.key === k);
            return section?.fieldOrder?.some(fk => BILINGUAL_FIELD_KEYS.has(fk));
        }),
        [sectionKeys, draft, sections, BILINGUAL_FIELD_KEYS]
    );

    const patch = useMemo(
        () => buildPatch(settings, draft, sectionKeys),
        [settings, draft, sectionKeys]
    );
    const isDirty = Object.keys(patch).length > 0;

    const handleSectionChange = (sectionKey, sectionValue) => {
        setDraft((prev) => ({ ...prev, [sectionKey]: sectionValue }));
    };

    const handleReset = () => setDraft(settings);

    const handleSave = () => {
        if (!isDirty) return;
        updateMutation.mutate(patch);
    };

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {title}
                    </Typography>
                    {description && (
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    )}
                </Box>
                {showLanguageToggle && !isLoading && !error && (
                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                )}
            </Stack>

            {isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : error ? (
                <Alert severity="error">
                    {isArabic ? 'تعذر تحميل الإعدادات' : 'Failed to load settings'}
                </Alert>
            ) : (
                <>
                    {sections.map((section) => {
                        const sectionData = draft?.[section.key];
                        const hasData = sectionData && Object.keys(sectionData).length > 0;
                        if (!hasData && !section.alwaysShow) return null;
                        return (
                            <SettingsSectionCard
                                key={section.key}
                                title={isArabic ? section.titleAr : section.titleEn}
                                description={isArabic ? section.descriptionAr : section.descriptionEn}
                                sectionKey={section.key}
                                sectionData={sectionData || {}}
                                onChange={handleSectionChange}
                                fieldOrder={section.fieldOrder}
                                disabled={updateMutation.isPending}
                                activeLang={activeLang}
                            />
                        );
                    })}

                    {sections.every((s) => !draft?.[s.key] && !s.alwaysShow) && (
                        <Alert severity="info">
                            {isArabic
                                ? 'لا توجد إعدادات لهذا القسم بعد. اطلب من فريق الباك إند تهيئة المفاتيح المطلوبة.'
                                : 'No settings exist for this section yet. Ask the backend team to seed the required keys.'}
                        </Alert>
                    )}

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        sx={{ mt: 3 }}
                        justifyContent="flex-end"
                    >
                        {extraActions}
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<RestartAlt />}
                            disabled={!isDirty || updateMutation.isPending}
                            onClick={handleReset}
                        >
                            {isArabic ? 'استعادة' : 'Reset'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            disabled={!isDirty || updateMutation.isPending}
                            onClick={handleSave}
                        >
                            {updateMutation.isPending
                                ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                                : (isArabic ? 'حفظ التغييرات' : 'Save changes')}
                        </Button>
                    </Stack>
                </>
            )}
        </WrapperSection>
    );
};

export default SettingsPage;
